<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-2">
      <card-title-row :straight-title="additionalModuleName" class="py-5" />
      <sidebar-form
        v-model="params"
        :is-valid.sync="isValid"
        ref="form"
        :inputs="inputs"
        :is-editing-on="!isStickyButtonsLoading"
      >
        <template #layer="scope">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="params.layer" v-bind="scope" />
            </v-col>
          </v-row>
        </template>
        <template #operation="scope">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="params.operation" v-bind="scope" />
            </v-col>
          </v-row>
        </template>
        <template #mask_layer="scope">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="params.mask_layer" v-bind="scope" />
            </v-col>
            <v-col cols="auto" class="ml-2">
              <v-row class="flex-column text-center" no-gutters>
                <v-col>
                  <dots-menu
                    :items="dotsItems"
                    @single="turnOnSelecting('single')"
                    @multiple="turnOnSelecting('multiple')"
                    @freehand="turnOnSelecting('freehand')"
                    is-absolute
                  >
                    <template #activator="{ on }">
                      <v-badge
                        :content="selectedFeaturesLength"
                        :value="!!selectedFeaturesLength"
                        color="primary"
                        overlap
                      >
                        <base-rounded-button
                          v-on="getButtonListeners(on)"
                          icon="$selection"
                          :color="buttonStyle['color']"
                          :icon-color="buttonStyle['iconColor']"
                          :has-shadow-effect="false"
                          :disabled="!params.mask_layer"
                          :tooltip="!params.mask_layer ? $i18n.t('sidebar.locationSelection.selectLayer') : ''"
                          disable-default-margin
                        />
                      </v-badge>
                    </template>
                  </dots-menu>
                </v-col>
                <v-col
                  v-if="!!selectedFeaturesLength"
                  style="cursor: pointer"
                  class="text-caption font-weight-light mt-1"
                  @click="clearSelection"
                >
                  {{ $i18n.t('sidebar.clear') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </template>
      </sidebar-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import SidebarForm from '@/components/SidebarForm';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
export default {
  name: 'LocationSelection',
  components: { CardTitleRow, SidebarForm, DataInput, DotsMenu },
  computed: {
    additionalModuleName: get('admin/additionalModules@SELECT_BY_LOCATION_MODULE.verbose_name'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    projectVectorLayers() {
      return this.projectLayers.filter(layer => layer.type === 'features_layer');
    },
    inputs() {
      return [
        {
          name: 'layer',
          dataType: { name: 'select' },
          items: this.projectVectorLayers.filter(layer => layer.id !== this.params.mask_layer),
          itemValue: 'id',
          itemText: 'name',
          translationPath: 'sidebar.locationSelection.selectFeaturesFromLayer',
          cols: '7',
          nameCols: '5',
          rules: [v => !!v || v === 0 || 'rules.required'],
          hideRulesIcon: true,
        },
        {
          name: 'operation',
          dataType: { name: 'select' },
          items: [
            { value: 'within', text: this.$i18n.t('sidebar.locationSelection.include') },
            { value: 'intersects', text: this.$i18n.t('sidebar.locationSelection.intersect') },
          ],
          translationPath: 'sidebar.locationSelection.which',
          cols: '7',
          nameCols: '5',
          rules: [v => !!v || v === 0 || 'rules.required'],
          hideRulesIcon: true,
        },
        {
          name: 'mask_layer',
          dataType: { name: 'select' },
          items: this.projectVectorLayers.filter(layer => layer.id !== this.params.layer),
          itemValue: 'id',
          itemText: 'name',
          translationPath: 'sidebar.locationSelection.withFeaturesFromLayer',
          cols: '7',
          nameCols: '5',
          rules: [v => !!v || v === 0 || 'rules.required'],
          hideRulesIcon: true,
        },
      ];
    },
    dataSources: get('layers/metadata'),
    selectedFeatures: get('map/selectedFeatures'),
    selectedFeaturesLength() {
      return this.selectedFeatures[this.params.mask_layer]?.length;
    },
    relationLayerdDataSourceName() {
      return this.projectVectorLayers.find(layer => layer.layer_id === this.params.mask_layer)?.data_source_name || '';
    },
    relationLayerdGeomAttrName() {
      return this.dataSources[this.relationLayerdDataSourceName]?.attributes_schema.geometry_name || '';
    },
    buttonStyle() {
      return this.isButtonActive
        ? {
            color: this.$_colors.primarybackground,
            iconColor: this.$_colors.primary,
          }
        : {
            color: 'rgba(0, 0, 0, 0.04)',
            iconColor: '#61646D',
          };
    },
  },
  data: () => ({
    isValid: false,
    params: {
      layer: null,
      mask_layer: null,
      operation: '',
    },
    isButtonActive: false,
    dotsItems: [
      {
        name: 'single',
      },
      {
        name: 'multiple',
      },
      {
        name: 'freehand',
      },
    ],
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getLocationSelection: call('layers/getLocationSelection'),
    deleteFeature(id, layerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    selectFeatures(layerId, results) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: results,
        },
      });
    },
    reset(closeTool = false) {
      if (closeTool) {
        const name = this.$route.name === 'locationSelectionLayer' ? 'layerTable' : 'map';
        this.$router.push({ name });
        return;
      }
      this.clearSelection();
      this.params = {
        layer: null,
        mask_layer: null,
        operation: '',
      };
      this.$refs?.form.$refs.form.resetValidation();
      if (this.$route.name !== 'locationSelection') {
        this.$router.push({ name: 'locationSelection' });
      }
    },
    async runSelection() {
      this.isStickyButtonsLoading = true;
      const payload = {
        ...this.params,
        mask_features: this.selectedFeatures[this.params.mask_layer],
      };
      const r = await this.getLocationSelection(payload);
      if (r.length > 0) {
        this.clearSelection();
        this.result = r;
        if (this.$route.params.lid) {
          this.deleteFeature(null, this.params.layer);
          this.selectFeatures(this.params.layer, this.result);
        } else {
          this.$router.push({
            name: 'locationSelectionLayer',
            params: { lid: this.params.layer },
          });
        }
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.locationSelectionNotFound'),
        });
      }
      this.isStickyButtonsLoading = false;
    },
    getButtonListeners(on) {
      return this.isButtonActive
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    async getSelectedFeatures(geometry) {
      return await this.getLayerFeatures({
        features_filter: {
          '%SPATIALLY_INTERSECTS': {
            [`${this.relationLayerdDataSourceName}.${this.relationLayerdGeomAttrName}`]: [geometry],
          },
        },
        layer_id: this.params.mask_layer,
        params: {
          ids_only: true,
        },
      });
    },
    turnOnSelecting(type) {
      if (type === 'single') {
        this.$root.$emit('attachOnClickSelection', this.params.mask_layer, false);
      } else if (type === 'multiple') {
        this.$root.$emit('selection-action', 'multiple', {
          layerId: this.params.mask_layer,
          selectionBoxAction: this.getSelectedFeatures,
        });
      } else {
        this.$root.$emit('selection-action', 'freehand', {
          layerId: this.params.mask_layer,
          selectionBoxAction: this.getSelectedFeatures,
        });
      }
      this.isButtonActive = true;
    },
    turnOffSelecting() {
      this.$root.$emit('deactivateAllTools');
      this.isButtonActive = false;
    },
    clearSelection() {
      this.turnOffSelecting();
      this.deleteFeature(null, this.params.mask_layer);
    },
  },
  watch: {
    'params.layer'() {
      if (this.$route.name === 'locationSelectionLayer') {
        this.$router.push({ name: 'locationSelection' });
      }
    },
    isValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
  },
  mounted() {
    this.$root.$off('runSelection');
    this.$root.$off('reset');
    this.$root.$on('runSelection', this.runSelection);
    this.$root.$on('reset', this.reset);
    this.stickyButtons = [
      {
        action: 'runSelection',
        translationPath: 'button.turnOn',
      },
      {
        action: 'reset',
        translationPath: 'button.reset',
        cancel: true,
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = false;
    this.isStickyButtonsVisible = true;
    this.$root.$on('tableDataRowsFetched', () => {
      this.selectFeatures(this.params.layer, this.result);
    });
  },
  beforeDestroy() {
    this.clearSelection();
    this.isStickyButtonsVisible = false;
    this.$root.$off('tableDataRowsFetched');
  },
};
</script>
